import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { BaseError } from '../../helpers/BaseError';

// constants
import { FormulaDetailModel, FormulaStatus } from '../../models/formula.model';

import API from '../../services/api';

// hooks
import useAnalytics from '../helper/useAnalytics';
import formulaKeys from './_queryKeys';

const useFormulaArchive = () => {
    const queryClient = useQueryClient();
    const catalogKey = ['formulaCatalog'];

    const { track } = useAnalytics();

    return useMutation<AxiosResponse<FormulaDetailModel>, BaseError, number>({
        mutationFn: API.archiveFormula,

        onMutate: async (formulaId) => {
            const previousAlternates = queryClient.getQueryData(catalogKey);

            queryClient.setQueryData(catalogKey, (previousData: any) => {
                if (!previousData) return previousData;

                const activeFormulas = previousData.active;
                let archivedFormula: FormulaDetailModel | undefined;

                Object.keys(activeFormulas).some((status) => {
                    activeFormulas[status] = activeFormulas[status].filter(
                        (formula: FormulaDetailModel) => {
                            const isMatch = formula.id === formulaId;
                            if (isMatch) {
                                archivedFormula = { ...formula };
                            }
                            return !isMatch;
                        },
                    );
                    return !!archivedFormula;
                });

                if (archivedFormula) {
                    archivedFormula.archived_at = new Date().toISOString();
                    archivedFormula.status = FormulaStatus.Archived;
                    archivedFormula.archived_by_id = 1; // dummy optimistic ID

                    previousData.inactive.Archived.unshift(archivedFormula);
                }

                return previousData;
            });

            return { previousAlternates };
        },

        onError: (_err, _formulaId, context: any) => {
            queryClient.setQueryData(catalogKey, context?.previousAlternates);
        },

        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: catalogKey,
            });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.all,
            });
        },

        onSuccess: (_data, formulaId) => {
            track('formula_archived', { formulaId });
        },
    });
};

export default useFormulaArchive;
